import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from "@angular/core";
import {AbstractVisbosControl} from "../../../core/visbos-core/ui/forms/abstract-visbos-control";
import {FormArray, FormBuilder, FormGroup, Validators} from "@angular/forms";
import {Warehousing} from "../../model/warehousing";
import {WarehousingReasonInterface} from "../../model/warehousing-reason-interface.type";
import {WarehousingSupplierInterface} from "../../model/warehousing-supplier-interface.type";
import {Stock} from "../../../shared/view-model/stock";
import {ApolloQueryObservable} from "apollo-angular";
import {SessionActions} from "../../../shared/actions/session.actions";
import {AdministrationUnitService} from "../../../organization/services/administration_unit_service/administration-unit.service";
import {FleetService} from "../../../fleet/services/fleet.service";
import {MedicineAuthFormComponent} from "../../withdrawal/medicine-auth-form/medicine-auth-form.component";
import {MissionFormComponent} from "../../../mission/mission-form/mission-form.component";
import {appInjector} from "../../../index";
import {SupplierService} from "../../../crm/services/supplier/supplier.service";
import {GERMAN_LAYOUT} from "../../../core/visbos-core/ui/directives/on-screen-keyboard.directive";
import {KeyboardOptions} from "virtual-keyboard";
import {WAREHOUSING_REASONS} from "../../warehousing-reasons.enum";
import {SUPPLIER_TYPES} from "../../supplier-types.enum";
import {ClientService} from "../../../crm/services/client.service";

@Component({
  selector: 'vb-warehousing-data-form',
  templateUrl: './warehousing-data-form.component.html',
  styleUrls: ['./warehousing-data-form.component.css'],
  providers: [{provide: AbstractVisbosControl, useExisting: forwardRef(() => WarehousingDataFormComponent)}]
})
export class WarehousingDataFormComponent extends AbstractVisbosControl<Warehousing, FormGroup> implements OnInit, AfterViewInit, OnChanges {

  warehousingReasons = WAREHOUSING_REASONS;
  supplierTypes = SUPPLIER_TYPES;

  kbOptions: KeyboardOptions = {
    type: 'input',
    layout: 'custom',
    customLayout: GERMAN_LAYOUT
  };

  @Input()
  touchMode: boolean = false;

  @Input()
  warehousingReason: WarehousingReasonInterface;

  @Input()
  supplier: WarehousingSupplierInterface;

  @Input()
  increasedStocks = [];

  @Output()
  supplierChanged: EventEmitter<WarehousingSupplierInterface> = new EventEmitter();

  private _controlVisibility: { reason?: boolean, supplier?: boolean, increasedStocks?: boolean, medicineAuth?: boolean } = {
    reason: true,
    supplier: true,
    increasedStocks: true,
    medicineAuth: false
  };

  get controlVisibility(): { reason?: boolean; supplier?: boolean; increasedStocks?: boolean; medicineAuth?: boolean } {
    return this._controlVisibility;
  }

  @Input()
  set controlVisibility(value: { reason?: boolean; supplier?: boolean; increasedStocks?: boolean; medicineAuth?: boolean }) {
    this._controlVisibility = Object.assign(this.controlVisibility, value);
  }

  private supplierItemsQuery: ApolloQueryObservable<any>;
  supplierItems: any[];

  constructor(fb: FormBuilder, cd: ChangeDetectorRef, sessionActions: SessionActions,
              private adminUnitService: AdministrationUnitService,
              private fleetService: FleetService,
              private supplierService: SupplierService, private clientService: ClientService) {
    super(fb, cd, sessionActions);
  }

  ngOnInit() {
    super.ngOnInit();

    if (this.controlVisibility.medicineAuth && this.control) {
      this.createMedicineAuthComponent();
    }
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
    this.loadSupplierData();
    this.setMissionForm();
    if (this.control.pristine) {
      this.onSupplierTypeChanged();
      this.onReasonTypeChanged();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {

    super.ngOnChanges(changes);

    if (changes.controlVisibility && this.control) {
      let vis = changes.controlVisibility.currentValue;
      if (vis.medicineAuth) {
        this.createMedicineAuthComponent();
      } else {
        this.removeMedicineAuthComponent();
      }
    }

    if (changes.warehousingReason && this.control) {
      let reason = changes.warehousingReason.currentValue;
      let ctrl = this.control.get('warehousing') as FormGroup;
    }

    if ((changes.increasedStocks || changes.control) && this.control) {
      this.patchIncreasedStocks();
    }

  }

  private patchIncreasedStocks() {
    let incStocks = this.increasedStocks;
    let ctrl = this.control.get('warehousing.increased_stocks') as FormArray;
    ctrl.controls = [];

    incStocks.forEach(incSt => {
      ctrl.push(this.fb.group({
        cuid: AbstractVisbosControl.cuid(),
        stock_id: incSt.stock.cuid,
        amount: incSt.amount
      }));

      // how medicine form data
      if (!this.controlVisibility.medicineAuth && incSt.stock.registration_required) {
        this.controlVisibility = {medicineAuth: true};
      }
    });
  }

  buildControlContent(): FormGroup {
    return WarehousingDataFormComponent.initControl(this.fb);
  }

  public static initControl(fb: FormBuilder = null): FormGroup {
    fb = fb ? fb : appInjector().get(FormBuilder);

    let ctrl = fb.group({
      warehousing: fb.group({
        cuid: [null],
        administration_unit_id: [null, Validators.required],
        warehousing_timestamp: [null, Validators.required],
        warehousing_reason_type: [null, Validators.required],
        warehousing_note: [null],
        warehousing_reason_id: [null],
        supplier_type: [null],
        increased_stocks: fb.array([]),
      })
    });

    return ctrl;
  }

  public setDefaultValues(item?: Warehousing): { [controlKey: string]: any } | any {
    return {
      warehousing: {
        cuid: this.setValue(item, 'cuid', AbstractVisbosControl.cuid()),
        administration_unit_id: this.setValue(item, 'administration_unit_id', this.activeAdminUnitCuid),
        warehousing_timestamp: this.setValue(item, 'warehousing_timestamp', new Date()),
        warehousing_note: this.setValue(item, 'warehousing_note'),
        warehousing_reason_type: this.setValue(item, 'warehousing_reason_type', 'Sonstiges'),
        warehousing_reason_id: this.setValue(item, 'warehousing_reason.cuid'),
        supplier_type: this.setValue(item, 'supplier_type', 'Standort'),
      }
    };
  }

  onReasonTypeChanged() {
    this.control.get('warehousing.warehousing_reason_id').reset();
    this.setMissionForm();
  }

  private setMissionForm() {
    if (this.control.get('warehousing.warehousing_reason_type').value == 'Einsatz') {
      this.createMissionFormComponent();
    } else {
      this.removeMissionFormComponent();
    }
  }

  onSupplierTypeChanged() {
    let ctrl = this.control.get('warehousing') as FormGroup;
    let cType = ctrl.get('supplier_type').value;

    if (cType == null || cType == '') {
      ctrl.removeControl('supplier_id');
    } else {
      ctrl.addControl('supplier_id', this.fb.control(null, Validators.required));
      this.control.get('warehousing.supplier_id').reset();
      this.loadSupplierData();
    }

    if (cType == 'Standort') {
      ctrl.get('supplier_id').setValue(this.activeAdminUnitCuid);
    }

  }

  createMissionFormComponent() {
    let ctrl = MissionFormComponent.initControl(this.fb);
    this.control.addControl('missionData', ctrl);
    this.cd.detectChanges();
    this.control.get('warehousing.warehousing_reason_id').setValue(this.control.get('missionData.cuid').value);
  }

  removeMissionFormComponent() {
    this.control.removeControl('missionData');
  }

  createMedicineAuthComponent() {
    let ctrl = this.control as FormGroup;
    ctrl.addControl('medicineAuthData', MedicineAuthFormComponent.initControl(this.fb));
    this.cd.detectChanges();
  }

  removeMedicineAuthComponent() {
    let ctrl = this.control as FormGroup;
    ctrl.removeControl('medicineAuthData');
  }

  loadSupplierData() {
    let cType = this.control.get('warehousing.supplier_type').value;

    this.supplierItems = [];

    if (cType == 'Standort') {
      this.supplierItemsQuery = this.adminUnitService.getAdminUnitsForCurrentUser();
      this.dataSubscriptions.push(this.supplierItemsQuery.subscribe((aus: any) => this.supplierItems = aus));
    }

    if (cType == 'Fahrzeug') {
      this.supplierItemsQuery = this.fleetService.fetchVehicles();
      this.dataSubscriptions.push(this.supplierItemsQuery.subscribe(res => this.supplierItems = res.data.vehicles));
    }

    if (cType == 'Lieferant') {
      this.supplierItemsQuery = this.supplierService.getSupplierList();
      this.dataSubscriptions.push(this.supplierItemsQuery.subscribe((aus: any) => this.supplierItems = aus));
    }

    if (cType == 'Klient') {
      this.supplierItemsQuery = this.clientService.getClients();
      this.dataSubscriptions.push(this.supplierItemsQuery.subscribe((res: any) => this.supplierItems = res.data.clients));
    }

  }

  onSupplierChanged(supplier: WarehousingSupplierInterface) {
    this.supplierChanged.emit(supplier);
  }

}



// WEBPACK FOOTER //
// ./src/app/store-management/warehousing/warehousing-data-form/warehousing-data-form.component.ts